/**
 * Client for Radio Browser API via same-origin proxy (/api/radio-browser).
 * Never calls radio-browser.info from the browser (required for mainland China / GFW).
 */

import { cspSafeMediaUrl } from './cspMediaUrl';
import { markStreamBlocked, apiFetch, ApiError } from './apiFetchPolicy';
import { sanitizeRadioIconUrl, streamHostFromUrl } from './radioStreamGuard';

const API_BASE =
  (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '') + '/radio-browser';

async function fetchJson(path, options = {}) {
  let res;
  try {
    res = await apiFetch(`${API_BASE}${path}`, {
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) },
      credentials: 'same-origin',
      rawResponse: true,
    });
  } catch (e) {
    if (e instanceof ApiError && (e.status === 429 || e.status === 503)) {
      markStreamBlocked('radio-browser', e.retryAfterSec || 900);
    }
    if (e instanceof ApiError) {
      const bodyErr = e.body && typeof e.body === 'object' ? e.body.error || e.body.detail : null;
      throw new Error(bodyErr || `Radio API error (${e.status})`);
    }
    throw e;
  }
  const source = res.headers.get('X-Radio-Browser-Source') || '';
  const sourcesRaw = res.headers.get('X-Radio-Sources') || '';
  const fallback = source === 'fallback';
  const data = await res.json();
  const sources = sourcesRaw ? sourcesRaw.split(',').map((s) => s.trim()).filter(Boolean) : [];
  return { data, source, fallback, sources };
}

export async function fetchRadioCountries() {
  const { data } = await fetchJson('/countries');
  return data;
}

export async function fetchRadioStations({
  countryCode,
  limit = 30,
  tag = '',
  language = '',
  state = '',
  name = '',
  city = '',
  includeIptv = false,
} = {}) {
  const code = String(countryCode || '').trim().toUpperCase();
  if (!code) throw new Error('countryCode is required');

  function buildParams({ tag: t, language: lang, state: st, name: n, city: c }) {
    const params = new URLSearchParams({ countrycode: code, limit: String(limit) });
    if (t) params.set('tag', t);
    if (lang) params.set('language', lang);
    if (st) params.set('state', st);
    if (n) params.set('name', n);
    if (c) params.set('city', c);
    if (includeIptv) params.set('includeIptv', '1');
    return params;
  }

  function filterForCountry(stations) {
    const list = Array.isArray(stations) ? stations : [];
    const matched = list.filter((s) => {
      const cc = String(s?.countrycode || s?.countryCode || '').trim().toUpperCase();
      return !cc || cc === code;
    });
    return matched.length ? matched : list;
  }

  function looksLikeOfflineFallback(stations) {
    return (
      Array.isArray(stations) &&
      stations.length > 0 &&
      stations.length <= 3 &&
      stations.every((s) => String(s?.stationuuid || s?.uuid || '').startsWith('fallback-'))
    );
  }

  let { data, source, fallback, sources } = await fetchJson(
    `/stations?${buildParams({ tag, language, state, name, city })}`,
  );
  let stations = filterForCountry(data);

  const usedExtraFilters = Boolean(tag || language || state || name || city);
  if (usedExtraFilters && (fallback || looksLikeOfflineFallback(stations) || stations.length === 0)) {
    const retry = await fetchJson(`/stations?${buildParams({ tag: '', language: '', state: '', name: '', city: '' })}`);
    if (!retry.fallback || !fallback) {
      data = retry.data;
      source = retry.source;
      fallback = retry.fallback;
      sources = retry.sources;
      stations = filterForCountry(data);
    }
  }

  return {
    stations,
    source,
    fallback,
    sources: sources.length ? sources : [source].filter(Boolean),
  };
}

export async function fetchRadioLanguages(limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  const { data } = await fetchJson(`/languages?${params}`);
  return Array.isArray(data) ? data : [];
}

export async function fetchRadioTags(limit = 50) {
  const params = new URLSearchParams({ limit: String(limit) });
  const { data } = await fetchJson(`/tags?${params}`);
  return Array.isArray(data) ? data : [];
}

export async function fetchTopicRadioStations({
  tag = '',
  language = '',
  name = '',
  countryCode = '',
  limit = 40,
} = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (tag) params.set('tag', tag);
  if (language) params.set('language', language);
  if (name) params.set('name', name);
  if (countryCode) params.set('countrycode', String(countryCode).trim().toUpperCase());

  try {
    const { data, source, fallback, sources } = await fetchJson(`/stations/topic?${params}`);
    return {
      stations: Array.isArray(data) ? data : [],
      source,
      fallback,
      sources: sources.length ? sources : [source].filter(Boolean),
    };
  } catch (err) {
    // Production backend may lag site repo and return 404 for /stations/topic.
    const msg = String(err?.message || err || '');
    if (!/404|not found/i.test(msg)) throw err;

    const fallbackParams = new URLSearchParams({ limit: String(limit) });
    fallbackParams.set('countrycode', String(countryCode || 'US').trim().toUpperCase());
    if (tag) fallbackParams.set('tag', tag);
    const { data, source, fallback, sources } = await fetchJson(`/stations?${fallbackParams}`);
    return {
      stations: Array.isArray(data) ? data : [],
      source,
      fallback,
      sources: sources.length ? sources : [source].filter(Boolean),
    };
  }
}

export async function registerRadioClick(stationUuid) {
  const uuid = String(stationUuid || '').trim();
  if (!uuid) return;
  try {
    await fetchJson(`/click/${encodeURIComponent(uuid)}`, { method: 'POST' });
  } catch {
    /* non-blocking */
  }
}

export function normalizeRadioStation(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const url = cspSafeMediaUrl(raw.url_resolved || raw.url);
  if (!url) return null;
  if (raw.playable === false) return null;
  const iconUrl = sanitizeRadioIconUrl(raw.iconUrl || '');
  const streamHost = raw.streamHost || streamHostFromUrl(url);
  return {
    uuid: raw.stationuuid || raw.uuid,
    name: raw.name || 'Unknown station',
    countryCode: raw.countrycode || '',
    country: raw.country || '',
    language: raw.language || '',
    tags: raw.tags || '',
    codec: raw.codec || '',
    bitrate: raw.bitrate || 0,
    iconUrl,
    url,
    streamHost,
    hls: /\.m3u8/i.test(url) || raw.hls === 1,
    source: raw.source || 'radio-browser',
    playable: raw.playable !== false,
    geoLat: raw.geo_lat ?? raw.geoLat ?? null,
    geoLong: raw.geo_long ?? raw.geoLong ?? null,
    clickCount: raw.clickcount ?? raw.clickCount ?? 0,
    fallback: String(raw.stationuuid || '').startsWith('fallback-'),
  };
}
